/**
 * ambientAudio.ts — (#sound) procedural ambient soundscape. NO audio files: every sound is
 * synthesized live with the Web Audio API from the same world state the renderer draws, so what
 * you hear always matches what you see — rain patter scales with the actual storm, crickets fade
 * in as the staged dusk lights come on, the dawn chorus rises with the sun, a train rumbles up
 * as one rolls through your viewport, and thunder follows the visible lightning.
 *
 * MUTED BY DEFAULT: the engine builds nothing until the user presses the 🔊 toggle (that click is
 * also the browser-autoplay unlock gesture). The choice persists in localStorage.
 *
 * Layers (all driven by ambientLevels(), a PURE function — unit-testable without a browser):
 *   rain    — white noise → bandpass hiss + lowpass body
 *   wind    — brown noise → lowpass, breathing LFO + random gust wander
 *   crick   — night crickets: pulsed sine chirps, 3 voices with their own cadence
 *   birds   — dawn-chorus chirp phrases (FM sweeps), stereo-scattered
 *   cicada  — summer-heat buzz: tremolo'd 6 kHz noise band
 *   train   — low rumble + slow clack tremolo, by real train proximity
 *   thunder — one-shot brown-noise rolls triggered by the storm's lightning flashes
 */
const clampN = (v: number, a = 0, b = 1) => Math.max(a, Math.min(b, v));

export interface AmbientState {
    weather: string; windMph: number; sun: number; hour: number;
    season: string; tempF: number; dark: number;   // dark = scene darkness() 0..1
    trainProx: number;                              // 0..1: nearest MOVING train's closeness to screen centre
    fountProx: number;                              // 0..1: plaza fountain's closeness (0 = none built yet)
    arcadeGlow: number;                             // 0..1: arcade closeness × its neon being lit
    crowdLvl: number;                               // 0..1: gathered-pets murmur (concert audience / busy café)
    wet: number;                                    // 0..1 ground wetness (scene #wx2 state)
    festOn: boolean;                                // a festival is on tonight (lanterns are strung)
    moonFrac?: number;                              // 0 new → 1 full (the owl is chattier under a full moon)
}
export interface Levels {
    rain: number; wind: number; crick: number; birds: number; cicada: number; train: number;
    fount: number; crowd: number; arcade: number; drip: number; owl: number; rooster: number; chime: number;
}

/** World state → per-layer loudness (0..1). Pure: same inputs, same mix, no audio objects. */
export function ambientLevels(s: AmbientState): Levels {
    const w = s.weather, storm = w === 'storm', raining = w === 'rain' || storm;
    const rain = storm ? 0.95 : w === 'rain' ? clampN(0.45 + s.windMph / 40, 0.45, 0.75) : 0;
    const wind = clampN((s.windMph - 3) / 26) * (storm ? 1 : 0.7) + (w === 'leaves' ? 0.18 : 0) + (w === 'snow' ? 0.15 : 0);
    const crick = s.dark * (s.season === 'winter' ? 0.1 : 1) * (raining ? 0.15 : 1) * (w === 'snow' ? 0.2 : 1);
    const dawnBell = Math.exp(-((s.hour - 6.5) ** 2) / 2.2);                       // dawn chorus peaks ~6:30
    const birds = Math.max(dawnBell, s.sun > 0.45 ? 0.25 : 0) * (1 - s.dark) * (raining ? 0 : 1) * (s.season === 'winter' ? 0.25 : 1);
    const cicada = w === 'heat' ? clampN((s.tempF - 78) / 18, 0.3, 1) * (1 - s.dark * 0.7) : 0;
    const train = clampN(s.trainProx) ** 1.3 * 0.9;   // gentle falloff: a train crossing the screen is clearly heard
    const fount = clampN(s.fountProx) ** 2.2;                                       // water burble only when really close
    const crowd = clampN(s.crowdLvl);                                               // murmur precombined by the scene
    const arcade = clampN(s.arcadeGlow);                                            // bleeps only while the neon's lit
    const drip = raining ? 0 : clampN(s.wet * 1.1) * 0.9;                           // gutters drip AFTER the rain
    const owl = (s.dark > 0.8 && !raining && w !== 'snow' ? (s.season === 'winter' ? 0.4 : 0.8) : 0) * (0.6 + 0.7 * (s.moonFrac ?? 0.5));
    const rooster = s.hour >= 5.6 && s.hour <= 7.4 && !raining ? 0.8 : 0;           // one bird greets the dawn
    const chime = s.festOn ? clampN(0.35 + s.windMph / 25) : 0;                     // lantern wind-chimes on festival nights
    return {
        rain: clampN(rain), wind: clampN(wind), crick: clampN(crick), birds: clampN(birds), cicada: clampN(cicada), train: clampN(train),
        fount: clampN(fount), crowd: clampN(crowd), arcade: clampN(arcade), drip: clampN(drip), owl: clampN(owl), rooster: clampN(rooster), chime: clampN(chime),
    };
}

/** Layers that are scheduled one-shot phrases (no continuous bed gain to drive). */
const SCHEDULED = new Set<keyof Levels>(['birds', 'drip', 'owl', 'rooster', 'chime', 'arcade']);

class AmbientAudio {
    private ctx: AudioContext | null = null;
    private master: GainNode | null = null;
    private gains: Partial<Record<keyof Levels, GainNode>> = {};   // per-layer level gains
    private windWander: GainNode | null = null;
    private rainWander: GainNode | null = null;
    private nextRainDropT = 0;
    private crickVoices: { chop: GainNode; nextT: number }[] = [];
    private nextBirdT = 0;
    private nextDripT = 0; private nextOwlT = 0; private nextRoosterT = 0; private nextChimeT = 0; private nextArcT = 0; private nextBloopT = 0;
    private lastThunderT = -99;
    private lastSchedT = 0;
    private white: AudioBuffer | null = null;
    private brown: AudioBuffer | null = null;
    enabled = false;
    private zoomV = 1;   // (#zoomvol) camera-altitude master: 1 up close → 0.1 zoomed way out

    /** Scale ALL sound (beds + one-shots) by the camera zoom — the city gets quiet from up high. */
    setZoom(v: number): void { this.zoomV = clampN(v, 0.1, 1); }

    /** Toggle the soundscape. MUST be called from a user gesture when turning on (autoplay policy). */
    setEnabled(on: boolean): void {
        this.enabled = on;
        if (on) {
            this.build();
            if (this.ctx?.state === 'suspended') void this.ctx.resume();
            this.master?.gain.setTargetAtTime(0.9, this.ctx!.currentTime, 0.5);
        } else if (this.ctx && this.master) {
            this.master.gain.setTargetAtTime(0, this.ctx.currentTime, 0.15);
        }
    }
    /** Re-arm a suspended context from any later gesture (e.g. pref said ON before first click). */
    userGesture(): void { if (this.enabled && this.ctx?.state === 'suspended') void this.ctx.resume(); }

    /** Per-frame: smooth each layer toward its level (duck = 1-concertVol so the show owns the stage). */
    update(lv: Levels, duck = 1): void {
        if (!this.enabled || !this.ctx || this.ctx.state !== 'running') return;
        duck *= this.zoomV;   // (#zoomvol) altitude attenuates everything below
        const t = this.ctx.currentTime;
        const tgt: Partial<Record<keyof Levels, number>> = {
            rain: lv.rain * 0.50 * duck, wind: lv.wind * 0.55 * duck, crick: lv.crick * 0.08 * duck,
            cicada: lv.cicada * 0.12 * duck, train: lv.train * 0.85 * duck,
            fount: lv.fount * 0.05 * duck, crowd: lv.crowd * 0.22 * duck,
        };
        for (const k of Object.keys(tgt) as (keyof Levels)[]) {
            if (SCHEDULED.has(k)) continue;                               // those are one-shot phrases, not beds
            this.gains[k]?.gain.setTargetAtTime(tgt[k]!, t, 0.4);
        }
        if (t - this.lastSchedT < 0.1) return;                            // scheduling work at ~10 Hz is plenty
        this.lastSchedT = t;
        // wind gusts: wander the lowpass bed a little, harder in heavy wind
        this.windWander?.gain.setTargetAtTime(1 + (Math.random() - 0.5) * 0.5 * (0.4 + lv.wind), t, 1.2);
        // rain sheets: the bed swells and eases on a slow random walk (kills the static-constant feel)
        this.rainWander?.gain.setTargetAtTime(0.8 + (Math.random() - 0.5) * 0.55, t, 2.2);
        // surface droplets: the audible TEXTURE of rain — little ticks/plops, denser when heavier
        if (lv.rain > 0.05 && t >= this.nextRainDropT) { this.rainDrop(lv.rain * duck); this.nextRainDropT = t + 0.10 + Math.random() * 0.5 / (0.2 + lv.rain); }
        // crickets: each voice chirps on its own clock while it's their hour
        if (lv.crick > 0.02) for (const v of this.crickVoices) {
            if (t < v.nextT) continue;
            const pulses = 3 + Math.floor(Math.random() * 3);
            for (let k = 0; k < pulses; k++) {                            // a burst of ~24 Hz wing pulses
                const p0 = t + 0.02 + k * 0.042;
                v.chop.gain.setValueAtTime(1, p0); v.chop.gain.setTargetAtTime(0, p0 + 0.016, 0.005);
            }
            v.nextT = t + 1.4 + Math.random() * 5;   // unhurried: each voice chirps every ~1.5–6.5 s
        }
        // birds: an occasional little phrase, panned somewhere in the trees
        if (lv.birds > 0.05 && t >= this.nextBirdT) { this.birdPhrase(lv.birds * duck); this.nextBirdT = t + 3 + Math.random() * 9 * (1.2 - lv.birds); }
        // fountain bloops: the actual "water" sound — little rising-pitch droplets, a few per second up close
        if (lv.fount > 0.05 && t >= this.nextBloopT) { this.waterBloop(lv.fount * duck); this.nextBloopT = t + 1.5 + Math.random() * 5.5 / (0.25 + lv.fount); }
        // post-rain gutter drips — sparse plips while the ground is still wet
        if (lv.drip > 0.08 && t >= this.nextDripT) { this.plip(lv.drip * duck); this.nextDripT = t + 1.2 + Math.random() * 4; }
        // a rare owl deep in the night, and one rooster greeting the dawn
        if (lv.owl > 0.05 && t >= this.nextOwlT) { this.owlHoot(lv.owl * duck); this.nextOwlT = t + 25 + Math.random() * 50; }
        if (lv.rooster > 0.05 && t >= this.nextRoosterT) { this.roosterCrow(lv.rooster * duck); this.nextRoosterT = t + 35 + Math.random() * 60; }
        // festival lantern wind-chimes — the breeze decides the tempo
        if (lv.chime > 0.04 && t >= this.nextChimeT) { this.chimeTinkle(lv.chime * duck); this.nextChimeT = t + 2 + Math.random() * 7 / (0.3 + lv.chime); }
        // faint retro bleeps drifting out of the lit arcade
        if (lv.arcade > 0.05 && t >= this.nextArcT) { this.arcadeBlip(lv.arcade * duck); this.nextArcT = t + 1.5 + Math.random() * 5; }
    }

    /** (#bell) The clocktower strikes the hour: `count` bell tones ~1.7 s apart (vol 0..1).
     *  Each strike = 4 inharmonic partials with slow decays + a tick of strike noise. */
    bell(count: number, vol: number): void {
        if (!this.enabled || !this.ctx || !this.master || this.ctx.state !== 'running') return;
        const ctx = this.ctx, v = clampN(vol) * this.zoomV, f0 = 349;
        for (let i = 0; i < Math.min(12, count); i++) {
            const at = ctx.currentTime + 0.05 + i * 1.7;
            const parts: [number, number][] = [[1, 0.40], [2.0, 0.22], [2.92, 0.13], [4.18, 0.07]];
            for (let pi = 0; pi < parts.length; pi++) {
                const [ratio, amp] = parts[pi]!;
                const o = ctx.createOscillator(); o.frequency.value = f0 * ratio;
                const g = ctx.createGain(); g.gain.setValueAtTime(0, at);
                g.gain.linearRampToValueAtTime(amp * v, at + 0.012);
                g.gain.setTargetAtTime(0, at + 0.03, 1.1 / (pi + 1));        // low partials ring longest
                o.connect(g); g.connect(this.master!); o.start(at); o.stop(at + 4);
                o.onended = () => { try { o.disconnect(); g.disconnect(); } catch { /* */ } };
            }
            if (this.white) {                                                 // the clapper's tick
                const s = ctx.createBufferSource(); s.buffer = this.white;
                const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 2600; bp.Q.value = 1;
                const g = ctx.createGain(); g.gain.setValueAtTime(0.06 * v, at); g.gain.setTargetAtTime(0, at + 0.005, 0.015);
                s.connect(bp); bp.connect(g); g.connect(this.master!); s.start(at); s.stop(at + 0.2);
                s.onended = () => { try { s.disconnect(); bp.disconnect(); g.disconnect(); } catch { /* */ } };
            }
        }
    }

    /** (#crowd) A cheer swell — the audience greets the band / sends them off (vol 0..1). */
    cheer(vol: number): void {
        if (!this.enabled || !this.ctx || !this.master || !this.white || this.ctx.state !== 'running') return;
        const ctx = this.ctx, at = ctx.currentTime + 0.02, v = clampN(vol) * this.zoomV;
        const mk = (freq0: number, freq1: number, peak: number) => {
            const s = ctx.createBufferSource(); s.buffer = this.white!; s.playbackRate.value = 0.9;
            const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 0.8;
            bp.frequency.setValueAtTime(freq0, at); bp.frequency.exponentialRampToValueAtTime(freq1, at + 0.5);
            const g = ctx.createGain(); g.gain.setValueAtTime(0, at);
            g.gain.linearRampToValueAtTime(peak, at + 0.28);                 // the roar rises…
            g.gain.setTargetAtTime(0, at + 0.9, 0.55);                       // …and washes out
            s.connect(bp); bp.connect(g); g.connect(this.master!); s.start(at); s.stop(at + 3.5);
            s.onended = () => { try { s.disconnect(); bp.disconnect(); g.disconnect(); } catch { /* */ } };
        };
        mk(950, 1500, 0.30 * v);   // voices
        mk(420, 520, 0.16 * v);    // body
    }

    /** (#party) A little party-horn toot + pop when a surprise party bursts nearby. */
    partyHorn(vol: number): void {
        if (!this.enabled || !this.ctx || !this.master || this.ctx.state !== 'running') return;
        const ctx = this.ctx, at = ctx.currentTime + 0.02, v = clampN(vol) * this.zoomV;
        if (this.white) {                                                     // the popper
            const s = ctx.createBufferSource(); s.buffer = this.white;
            const g = ctx.createGain(); g.gain.setValueAtTime(0.12 * v, at); g.gain.setTargetAtTime(0, at + 0.008, 0.02);
            s.connect(g); g.connect(this.master); s.start(at); s.stop(at + 0.15);
            s.onended = () => { try { s.disconnect(); g.disconnect(); } catch { /* */ } };
        }
        const o = ctx.createOscillator(); o.type = 'square';                  // the toot
        o.frequency.setValueAtTime(285, at + 0.05); o.frequency.linearRampToValueAtTime(345, at + 0.3);
        const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 1400;
        const g = ctx.createGain(); g.gain.setValueAtTime(0, at + 0.05);
        g.gain.linearRampToValueAtTime(0.10 * v, at + 0.09); g.gain.setTargetAtTime(0, at + 0.3, 0.05);
        o.connect(lp); lp.connect(g); g.connect(this.master); o.start(at + 0.05); o.stop(at + 0.6);
        o.onended = () => { try { o.disconnect(); lp.disconnect(); g.disconnect(); } catch { /* */ } };
    }

    /** (#train) Air-brake hiss as a train stops or pulls out (vol = its proximity 0..1).
     *  Arrival: one long PSSSH as the brakes set. Departure: psh..PSSH as they release and it
     *  pulls away. White noise through a falling bandpass = the gas-release character. */
    trainHiss(vol: number, arriving: boolean): void {
        if (!this.enabled || !this.ctx || !this.master || !this.white || this.ctx.state !== 'running') return;
        const ctx = this.ctx, t0 = ctx.currentTime + 0.02;
        const mk = (at: number, dur: number, peak: number, freq: number) => {
            const src = ctx.createBufferSource(); src.buffer = this.white!;
            const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 0.9;
            bp.frequency.setValueAtTime(freq, at); bp.frequency.exponentialRampToValueAtTime(freq * 0.5, at + dur);
            const g = ctx.createGain(); g.gain.setValueAtTime(0, at);
            g.gain.linearRampToValueAtTime(peak, at + 0.035);
            g.gain.setTargetAtTime(0, at + dur * 0.35, dur * 0.3);
            src.connect(bp); bp.connect(g); g.connect(this.master!);
            src.start(at); src.stop(at + dur + 0.6);
            src.onended = () => { try { src.disconnect(); bp.disconnect(); g.disconnect(); } catch { /* */ } };
        };
        const v = clampN(vol) * 0.25 * this.zoomV;
        if (arriving) mk(t0, 1.5, v, 1500);
        else { mk(t0, 0.5, v * 0.75, 1850); mk(t0 + 0.45, 1.0, v, 1150); }
    }

    /** A thunder strike (intensity 0..1, delayed like real thunder): a close strike gets a sharp
     *  CRACK before the roll; every strike gets a long roll + a late echo rumble. Thunder fills
     *  the sky, so the zoom-out master only attenuates it to 50%, not 10%. Refractory-paced. */
    thunder(intensity: number, delayMs: number): void {
        if (!this.enabled || !this.ctx || !this.master || !this.brown) return;
        const t = this.ctx.currentTime;
        if (t - this.lastThunderT < 6 + Math.random() * 5) return;
        this.lastThunderT = t;
        intensity *= Math.max(this.zoomV, 0.5);   // (#zoomvol) sky-wide sound — altitude barely dims it
        const ctx = this.ctx, master = this.master, at = t + delayMs / 1000;
        const close = intensity > 0.45;
        if (close && this.white) {   // the CRACK: a snap of bright noise right at the front edge
            const c = ctx.createBufferSource(); c.buffer = this.white; c.playbackRate.value = 1.1;
            const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 900;
            const cg = ctx.createGain(); cg.gain.setValueAtTime(0.5 * intensity, at);
            cg.gain.setTargetAtTime(0, at + 0.02, 0.06);
            c.connect(hp); hp.connect(cg); cg.connect(master);
            c.start(at); c.stop(at + 0.5);
            c.onended = () => { try { c.disconnect(); hp.disconnect(); cg.disconnect(); } catch { /* */ } };
        }
        const roll = (rAt: number, peak: number, rate: number, f0: number, f1: number, tail: number) => {
            const src = ctx.createBufferSource(); src.buffer = this.brown!; src.playbackRate.value = rate;
            const lp = ctx.createBiquadFilter(); lp.type = 'lowpass';
            lp.frequency.setValueAtTime(f0, rAt); lp.frequency.exponentialRampToValueAtTime(f1, rAt + 2.5);
            const g = ctx.createGain(); g.gain.setValueAtTime(0, rAt);
            g.gain.linearRampToValueAtTime(peak, rAt + 0.07);
            g.gain.setTargetAtTime(peak * 0.3, rAt + 0.3, 0.5);
            g.gain.setTargetAtTime(0, rAt + 1.4, tail);
            src.connect(lp); lp.connect(g); g.connect(master);
            src.start(rAt); src.stop(rAt + 7);
            src.onended = () => { try { src.disconnect(); lp.disconnect(); g.disconnect(); } catch { /* */ } };
        };
        roll(at, 0.8 * intensity, 0.55 + Math.random() * 0.35, 200, 55, 1.3);                       // the main roll
        roll(at + 1.6 + Math.random() * 0.8, 0.22 * intensity, 0.45, 110, 45, 1.6);                 // …and the distant echo
    }

    // ---- graph construction (once, on first enable) ----
    private build(): void {
        if (this.ctx) return;
        const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = this.ctx = new AC();
        // a gentle compressor glues the layers + stops stacked weather from clipping
        const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -20; comp.ratio.value = 4; comp.connect(ctx.destination);
        this.master = ctx.createGain(); this.master.gain.value = 0; this.master.connect(comp);
        // shared noise sources
        this.white = this.noiseBuf(2, false); this.brown = this.noiseBuf(4, true);
        const loop = (buf: AudioBuffer) => { const s = ctx.createBufferSource(); s.buffer = buf; s.loop = true; s.start(); return s; };
        const layer = (k: keyof Levels) => { const g = ctx.createGain(); g.gain.value = 0; g.connect(this.master!); this.gains[k] = g; return g; };
        // rain: a DARKER hiss + low body under a slow wave modulation (rain comes in sheets, not a
        // constant static), with the surface-droplet ticks scheduled in update() carrying the texture
        const rainG = layer('rain');
        const rWander = ctx.createGain(); rWander.gain.value = 0.8; this.rainWander = rWander;
        for (const [hz, depth] of [[0.05, 0.22], [0.013, 0.15]] as [number, number][]) {   // overlapping sheets
            const o = ctx.createOscillator(); o.frequency.value = hz;
            const d = ctx.createGain(); d.gain.value = depth; o.connect(d); d.connect(rWander.gain); o.start();
        }
        const hiss = ctx.createBiquadFilter(); hiss.type = 'bandpass'; hiss.frequency.value = 1150; hiss.Q.value = 0.7;
        const hissG = ctx.createGain(); hissG.gain.value = 0.75;
        const body = ctx.createBiquadFilter(); body.type = 'lowpass'; body.frequency.value = 320;
        const bodyG = ctx.createGain(); bodyG.gain.value = 0.8;
        loop(this.white).connect(hiss); hiss.connect(hissG); hissG.connect(rWander);
        loop(this.white).connect(body); body.connect(bodyG); bodyG.connect(rWander);
        rWander.connect(rainG);
        // wind: brown noise → lowpass, breathing LFO on top of the random wander gain
        const windG = layer('wind');
        const wlp = ctx.createBiquadFilter(); wlp.type = 'lowpass'; wlp.frequency.value = 240;
        this.windWander = ctx.createGain(); this.windWander.gain.value = 1;
        const lfoG = ctx.createGain(); lfoG.gain.value = 0.3;                                   // breathing depth
        const lfo = ctx.createOscillator(); lfo.frequency.value = 0.07; lfo.connect(lfoG); lfoG.connect(this.windWander.gain); lfo.start();
        loop(this.brown).connect(wlp); wlp.connect(this.windWander); this.windWander.connect(windG);
        // crickets: 3 pulsed sine voices at slightly different pitches
        const crickG = layer('crick');
        for (let i = 0; i < 3; i++) {
            const osc = ctx.createOscillator(); osc.frequency.value = 4150 + i * 230;
            const chop = ctx.createGain(); chop.gain.value = 0;
            osc.connect(chop); chop.connect(crickG); osc.start();
            this.crickVoices.push({ chop, nextT: 0 });
        }
        // cicada: 6 kHz noise band with a fast tremolo (the summer power-line buzz)
        const cicG = layer('cicada');
        const cbp = ctx.createBiquadFilter(); cbp.type = 'bandpass'; cbp.frequency.value = 5900; cbp.Q.value = 4;
        const trem = ctx.createGain(); trem.gain.value = 0.5;
        const tlfo = ctx.createOscillator(); tlfo.frequency.value = 26;
        const tdep = ctx.createGain(); tdep.gain.value = 0.5; tlfo.connect(tdep); tdep.connect(trem.gain); tlfo.start();
        loop(this.white).connect(cbp); cbp.connect(trem); trem.connect(cicG);
        // train: deep rumble + a MID-frequency rail voice (so it carries on laptop/phone speakers,
        // where a 110 Hz-only bed is inaudible), under a slow clackety tremolo
        const trG = layer('train');
        const tlp = ctx.createBiquadFilter(); tlp.type = 'lowpass'; tlp.frequency.value = 200;
        const tmid = ctx.createBiquadFilter(); tmid.type = 'bandpass'; tmid.frequency.value = 480; tmid.Q.value = 1.1;
        const tmidG = ctx.createGain(); tmidG.gain.value = 0.4;
        const ttrem = ctx.createGain(); ttrem.gain.value = 0.8;
        const tt = ctx.createOscillator(); tt.frequency.value = 2.8;
        const ttd = ctx.createGain(); ttd.gain.value = 0.28; tt.connect(ttd); ttd.connect(ttrem.gain); tt.start();
        loop(this.brown).connect(tlp); tlp.connect(ttrem);
        loop(this.brown).connect(tmid); tmid.connect(tmidG); tmidG.connect(ttrem);
        ttrem.connect(trG);
        // fountain: a DARK, quiet trickle bed (lowpassed, no hiss) — the watery identity comes
        // from the discrete rising-pitch bloops scheduled in update(), not from noise
        const fG = layer('fount');
        const flp = ctx.createBiquadFilter(); flp.type = 'lowpass'; flp.frequency.value = 480;
        const fwob = ctx.createGain(); fwob.gain.value = 0.6;
        for (const [hz, depth] of [[1.3, 0.18], [0.37, 0.12]] as [number, number][]) {
            const o = ctx.createOscillator(); o.frequency.value = hz;
            const d = ctx.createGain(); d.gain.value = depth; o.connect(d); d.connect(fwob.gain); o.start();
        }
        loop(this.white).connect(flp); flp.connect(fwob); fwob.connect(fG);
        // crowd murmur: speech-band noise with a slow wander on the band centre (no real voices)
        const cG = layer('crowd');
        const mbp = ctx.createBiquadFilter(); mbp.type = 'bandpass'; mbp.frequency.value = 450; mbp.Q.value = 0.5;
        const mlp = ctx.createBiquadFilter(); mlp.type = 'lowpass'; mlp.frequency.value = 1800;
        const mlfo = ctx.createOscillator(); mlfo.frequency.value = 0.31;
        const mld = ctx.createGain(); mld.gain.value = 130; mlfo.connect(mld); mld.connect(mbp.frequency); mlfo.start();
        loop(this.white).connect(mbp); mbp.connect(mlp); mlp.connect(cG);
    }

    // ---- scheduled one-shot voices ----
    /** A rain droplet hitting a surface: a tiny damped tick, sometimes a softer low plop. */
    private rainDrop(lvl: number): void {
        if (!this.ctx || !this.master || !this.white) return;
        const ctx = this.ctx, at = ctx.currentTime + 0.005;
        if (Math.random() < 0.75) {   // tick on a hard surface
            const s = ctx.createBufferSource(); s.buffer = this.white; s.playbackRate.value = 0.8 + Math.random() * 0.5;
            const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 2200 + Math.random() * 1800; bp.Q.value = 2.2;
            const g = ctx.createGain(); g.gain.setValueAtTime((0.015 + Math.random() * 0.03) * lvl, at);
            g.gain.setTargetAtTime(0, at + 0.006, 0.012);
            s.connect(bp); bp.connect(g); g.connect(this.master); s.start(at); s.stop(at + 0.08);
            s.onended = () => { try { s.disconnect(); bp.disconnect(); g.disconnect(); } catch { /* */ } };
        } else {                      // plop into a puddle
            const o = ctx.createOscillator(); o.frequency.setValueAtTime(750 + Math.random() * 350, at);
            o.frequency.exponentialRampToValueAtTime(420, at + 0.05);
            const g = ctx.createGain(); g.gain.setValueAtTime(0, at);
            g.gain.linearRampToValueAtTime(0.025 * lvl, at + 0.006); g.gain.setTargetAtTime(0, at + 0.02, 0.02);
            o.connect(g); g.connect(this.master); o.start(at); o.stop(at + 0.15);
            o.onended = () => { try { o.disconnect(); g.disconnect(); } catch { /* */ } };
        }
    }

    /** A water bloop: the classic droplet signature — a tiny sine chirp RISING in pitch. */
    private waterBloop(lvl: number): void {
        if (!this.ctx || !this.master) return;
        const ctx = this.ctx, at = ctx.currentTime + 0.01;
        const f0 = 320 + Math.random() * 420, dur = 0.04 + Math.random() * 0.07;
        const o = ctx.createOscillator(); o.frequency.setValueAtTime(f0, at);
        o.frequency.exponentialRampToValueAtTime(f0 * (1.7 + Math.random() * 0.9), at + dur);
        const g = ctx.createGain(); g.gain.setValueAtTime(0, at);
        g.gain.linearRampToValueAtTime((0.015 + Math.random() * 0.02) * lvl, at + 0.006);
        g.gain.setTargetAtTime(0, at + dur * 0.6, 0.03);
        o.connect(g); g.connect(this.master); o.start(at); o.stop(at + dur + 0.15);
        o.onended = () => { try { o.disconnect(); g.disconnect(); } catch { /* */ } };
    }

    /** A gutter drip: a tiny falling plip (sometimes a quick second one). */
    private plip(lvl: number): void {
        if (!this.ctx || !this.master) return;
        const ctx = this.ctx;
        const one = (at: number, peak: number) => {
            const o = ctx.createOscillator(); o.frequency.setValueAtTime(1250, at); o.frequency.exponentialRampToValueAtTime(620, at + 0.06);
            const g = ctx.createGain(); g.gain.setValueAtTime(0, at);
            g.gain.linearRampToValueAtTime(peak, at + 0.008); g.gain.setTargetAtTime(0, at + 0.02, 0.025);
            o.connect(g); g.connect(this.master!); o.start(at); o.stop(at + 0.2);
            o.onended = () => { try { o.disconnect(); g.disconnect(); } catch { /* */ } };
        };
        one(ctx.currentTime + 0.02, 0.09 * lvl);
        if (Math.random() < 0.3) one(ctx.currentTime + 0.16, 0.05 * lvl);
    }

    /** A distant two-note owl: hoo… HOO (low vibrato sines through a lowpass). */
    private owlHoot(lvl: number): void {
        if (!this.ctx || !this.master) return;
        const ctx = this.ctx, t0 = ctx.currentTime + 0.05;
        const note = (at: number, dur: number, f: number, peak: number) => {
            const o = ctx.createOscillator(); o.frequency.value = f;
            const vib = ctx.createOscillator(); vib.frequency.value = 5.5;
            const vd = ctx.createGain(); vd.gain.value = 5; vib.connect(vd); vd.connect(o.frequency); vib.start(at); vib.stop(at + dur + 0.3);
            const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 850;
            const g = ctx.createGain(); g.gain.setValueAtTime(0, at);
            g.gain.linearRampToValueAtTime(peak, at + 0.06); g.gain.setTargetAtTime(0, at + dur * 0.55, dur * 0.35);
            o.connect(lp); lp.connect(g); g.connect(this.master!); o.start(at); o.stop(at + dur + 0.4);
            o.onended = () => { try { o.disconnect(); lp.disconnect(); g.disconnect(); vd.disconnect(); } catch { /* */ } };
        };
        note(t0, 0.3, 335, 0.07 * lvl);
        note(t0 + 0.5, 0.55, 316, 0.09 * lvl);
    }

    /** One far-off rooster: a cartoony four-syllable crow through a horn-ish bandpass. */
    private roosterCrow(lvl: number): void {
        if (!this.ctx || !this.master) return;
        const ctx = this.ctx, at = ctx.currentTime + 0.05, v = 0.055 * lvl;
        const o = ctx.createOscillator(); o.type = 'sawtooth';
        const f = o.frequency;
        f.setValueAtTime(540, at); f.linearRampToValueAtTime(760, at + 0.14); f.linearRampToValueAtTime(640, at + 0.27);
        f.linearRampToValueAtTime(1020, at + 0.5); f.linearRampToValueAtTime(560, at + 1.05);
        const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1150; bp.Q.value = 1.4;
        const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2400;
        const g = ctx.createGain(); const seg = [[0, 0], [0.05, v], [0.13, v * 0.3], [0.18, v], [0.26, v * 0.3], [0.31, v * 1.2], [0.55, v * 0.4], [0.62, v], [1.0, 0]] as [number, number][];
        g.gain.setValueAtTime(0, at); for (const [dt, amp] of seg) g.gain.linearRampToValueAtTime(amp, at + dt + 0.001);
        o.connect(bp); bp.connect(lp); lp.connect(g); g.connect(this.master); o.start(at); o.stop(at + 1.3);
        o.onended = () => { try { o.disconnect(); bp.disconnect(); lp.disconnect(); g.disconnect(); } catch { /* */ } };
    }

    /** Festival lantern chimes: 1–3 pentatonic tings, slightly detuned pairs, long ring. */
    private chimeTinkle(lvl: number): void {
        if (!this.ctx || !this.master) return;
        const ctx = this.ctx, notes = [1568, 1760, 2093, 2349, 2637];
        const n = 1 + (Math.random() < 0.35 ? Math.floor(Math.random() * 2) + 1 : 0);
        let at = ctx.currentTime + 0.03;
        for (let i = 0; i < n; i++) {
            const f = notes[Math.floor(Math.random() * notes.length)]!;
            for (const det of [1, 1.004]) {
                const o = ctx.createOscillator(); o.frequency.value = f * det;
                const g = ctx.createGain(); const peak = (0.03 + Math.random() * 0.03) * lvl;
                g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(peak, at + 0.006);
                g.gain.setTargetAtTime(0, at + 0.02, 0.6 + Math.random() * 0.8);
                o.connect(g); g.connect(this.master); o.start(at); o.stop(at + 3);
                o.onended = () => { try { o.disconnect(); g.disconnect(); } catch { /* */ } };
            }
            at += 0.09 + Math.random() * 0.13;
        }
    }

    /** Faint retro bleeps from the arcade: 1–3 rising square-wave notes. */
    private arcadeBlip(lvl: number): void {
        if (!this.ctx || !this.master) return;
        const ctx = this.ctx, scale = [262, 330, 392, 523, 659, 784];
        const n = 1 + Math.floor(Math.random() * 3); let idx = Math.floor(Math.random() * 3), at = ctx.currentTime + 0.02;
        for (let i = 0; i < n; i++) {
            const o = ctx.createOscillator(); o.type = 'square'; o.frequency.value = scale[Math.min(idx, scale.length - 1)]!;
            const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2600;
            const g = ctx.createGain(); g.gain.setValueAtTime(0, at);
            g.gain.linearRampToValueAtTime(0.035 * lvl, at + 0.005); g.gain.setValueAtTime(0.035 * lvl, at + 0.06); g.gain.linearRampToValueAtTime(0, at + 0.075);
            o.connect(lp); lp.connect(g); g.connect(this.master); o.start(at); o.stop(at + 0.15);
            o.onended = () => { try { o.disconnect(); lp.disconnect(); g.disconnect(); } catch { /* */ } };
            idx += 1 + Math.floor(Math.random() * 2); at += 0.09;
        }
    }

    /** One little bird phrase: 2–5 frequency-swept chirps, panned into the stereo field. */
    private birdPhrase(vol: number): void {
        if (!this.ctx || !this.master) return;
        const ctx = this.ctx, n = 2 + Math.floor(Math.random() * 4);
        const pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
        const out = ctx.createGain(); out.gain.value = 0.22 * vol;
        if (pan) { pan.pan.value = (Math.random() - 0.5) * 1.2; out.connect(pan); pan.connect(this.master); } else out.connect(this.master);
        let t = ctx.currentTime + 0.05;
        for (let i = 0; i < n; i++) {
            const f0 = 2300 + Math.random() * 1500, dur = 0.07 + Math.random() * 0.09;
            const o = ctx.createOscillator(); o.frequency.setValueAtTime(f0, t);
            o.frequency.exponentialRampToValueAtTime(f0 * (1.15 + Math.random() * 0.45), t + dur);
            const g = ctx.createGain(); g.gain.setValueAtTime(0, t);
            g.gain.linearRampToValueAtTime(1, t + 0.012); g.gain.setTargetAtTime(0, t + dur - 0.015, 0.02);
            o.connect(g); g.connect(out); o.start(t); o.stop(t + dur + 0.1);
            t += dur + 0.04 + Math.random() * 0.12;
        }
        setTimeout(() => { try { out.disconnect(); pan?.disconnect(); } catch { /* */ } }, (t - ctx.currentTime + 0.5) * 1000);
    }

    private noiseBuf(secs: number, brown: boolean): AudioBuffer {
        const ctx = this.ctx!, len = Math.floor(ctx.sampleRate * secs);
        const buf = ctx.createBuffer(1, len, ctx.sampleRate), d = buf.getChannelData(0);
        let last = 0;
        for (let i = 0; i < len; i++) {
            const w = Math.random() * 2 - 1;
            if (brown) { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.5; } else d[i] = w;
        }
        return buf;
    }
}

export const ambient = new AmbientAudio();
